'use client';

import heroBackground from '@/public/prices/heroBackground.png';
import Image from 'next/image';
import { easeInOut, motion } from 'framer-motion';
import { PortableTextBlock } from 'sanity';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { GROQImage } from '@/sanity/lib/types';
import HeroCard from '@/components/global/HeroCard';

interface IHeroProps {
  title: PortableTextBlock[];
  cardOne: {
    text: PortableTextBlock[];
    image: GROQImage;
  };
  cardTwo: {
    text: PortableTextBlock[];
    image: GROQImage;
  };
}

export default function Hero({
  title,
  cardOne,
  cardTwo,
}: IHeroProps) {
  // Helper function to check if text content is valid
  const hasValidText = (text: PortableTextBlock[] | null | undefined): boolean => {
    if (!text || !Array.isArray(text) || text.length === 0) return false;

    // Check if there's actual text content in the array
    return text.some((block: any) => {
      if (block.children && Array.isArray(block.children)) {
        return block.children.some((child: any) =>
          child.text && typeof child.text === 'string' && child.text.trim().length > 0
        );
      }
      return false;
    });
  };

  return (
    <section className="w-full flex flex-col items-center lg:pt-[3rem]">
      <div className="relative flex justify-between flex-col w-full max-w-[96vw] lg:max-w-[230.01rem] lg:min-h-[117.74988rem] overflow-clip lg:px-[6rem] lg:py-[10rem] lg:items-start items-center">
        <div className="container w-full relative z-10 flex flex-col gap-[3rem] lg:gap-[9.13rem] lg:mt-auto pt-[38.44rem] lg:pt-0 px-[1rem] lg:px-0">
          <motion.div
            className="flex flex-col gap-[2rem] lg:gap-[2.25rem] lg:text-start text-center w-[min(75.125rem,100%)] mx-auto lg:mx-0
              prose-headings:text-[5.65rem] lg:prose-headings:text-[7.5rem] prose-headings:mb-0 prose-headings:leading-[110%] lg:prose-headings:leading-[120%] prose-em:text-[5.375rem] lg:prose-em:text-[8rem] prose-p:max-w-[98%] lg:prose-p:max-w-[67.375rem]"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: easeInOut, delay: 0.25 }}
            whileInView="visible"
            viewport={{ once: true }}
          >
            <PortableText value={title} components={richTextComponent} />
          </motion.div>

          <div className="flex flex-col lg:flex-row gap-[2rem] lg:gap-[3.62rem] items-center w-full">
            {hasValidText(cardOne.text) && (
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: easeInOut, delay: 0.45 }}
                viewport={{ once: true }}
                className="w-full lg:w-auto"
              >
                <HeroCard
                  text={cardOne.text}
                  image={cardOne.image}
                />
              </motion.div>
            )}
            {hasValidText(cardTwo.text) && (
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: easeInOut, delay: 0.65 }}
                viewport={{ once: true }}
                className="w-full lg:w-auto"
              >
                <HeroCard
                  text={cardTwo.text}
                  image={cardTwo.image}
                />
              </motion.div>
            )}
          </div>
        </div>

        {/* Background Start */}
        <motion.div
          initial={{ x: 0 }}
          animate={{ x: '-175vw'	 }}
          transition={{ duration: 1.8, ease: [0.76, 0, 0.24, 1] }}
          whileInView="visible"
          viewport={{ once: true }}
          className="absolute w-[150vw] h-full bg-gradient-to-r from-offwhite from-75% to-transparent left-0 z-[1] pointer-events-none select-none bottom-0"
        />

        <svg className="absolute w-[30.5rem] h-auto scale-[.82] lg:scale-100 top-[-3.5rem] lg:top-[-.125rem] right-[-19.75rem] lg:right-[-0.1rem] z-[1]" viewBox="0 0 243 106" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M242.08 105.999V0H0C2.20914 0 4.00003 1.79086 4.00003 4V98C4.00003 100.209 5.79103 102 8.00006 102H238.001C240.209 102 242 103.79 242.001 105.999H242.08Z" fill="#F6F4E9"/>
        </svg>

        <svg className="absolute bottom-[-0.2rem] right-[-0.1rem] z-[1] hidden lg:block" width="390" height="385" viewBox="0 0 390 385" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M389.569 -0.000239566C389.569 1.07046 389.139 2.09605 388.377 2.84771L2.72392 382.989C1.97553 383.727 1.05081 383.741 0 383.741H0.443848L389.569 384.143V-0.000239566Z" fill="#F6F4E9"/>
        </svg>

        <div className="absolute top-[1.25rem] left-0 w-full h-[56rem] pointer-events-none select-none bg-gradient-to-t from-offwhite via-transparent to-transparent lg:hidden z-[1]"/>

        <motion.div
          className="absolute inset-x-0 lg:inset-0 top-[1.25rem] lg:top-auto h-[55rem] lg:h-full z-0 pointer-events-none select-none overflow-clip"
          initial={{ scale: 2.5 }}
          animate={{ scale: 1 }}
          transition={{
            duration: 1.25,
            ease: easeInOut,
          }}
          viewport={{ once: true, margin: '-100px' }}
        >
          <Image
            src={heroBackground}
            alt="Hero Background"
            layout="fill"
            objectFit="cover"
            quality={100}
            loading="lazy"
            className="translate-y-[-3rem] lg:translate-y-0 object-[58%_50%] lg:object-center scale-[1.20] lg:scale-100 opacity-70 lg:opacity-100"
          />
        </motion.div>
        {/* Background End */}
      </div>
    </section>
  );
}
